import React, { useCallback, useEffect, useState } from 'react';
import AgentLayout from './AgentLayout';
import axios from 'axios';
import { useAgentAuth } from '../../context/AgentAuthContext';
import CreditSummaryCard from '../../components/agent/CreditSummaryCard';
import {
  TransactionsTable, TxnStatusBadge, DebitCell, CreditCell, BalanceCell, BookingLink,
} from '../../components/agent/TransactionsTable';
import {
  Wallet,
  Plus,
  Upload,
  X,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';

const AgentWallet = () => {
  const { agent, refreshWallet } = useAgentAuth();
  const [balance, setBalance] = useState(agent?.wallet_balance || 0);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [txnRefresh, setTxnRefresh] = useState(0);
  
  // Topup form state
  const [topupData, setTopupData] = useState({
    amount: '',
    bank_details: '',
    notes: ''
  });
  const [paymentProof, setPaymentProof] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  

  useEffect(() => {
    fetchWalletData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWalletData = async () => {
    try {
      const [balanceRes, requestsRes] = await Promise.all([
        axios.get(`${API_URL}/api/agent/wallet/balance`, { withCredentials: true }),
        axios.get(`${API_URL}/api/agent/wallet/requests`, { withCredentials: true })
      ]);
      setBalance(balanceRes.data.wallet_balance);
      await refreshWallet();
      setRequests(requestsRes.data.requests);
    } catch (error) {
      console.error('Error fetching wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Memoised pager used by TransactionsTable — translates its payload into API params
  const fetchTxnPage = useCallback(async (params) => {
    const { data } = await axios.get(`${API_URL}/api/agent/wallet/transactions`, {
      withCredentials: true,
      params: {
        page: params.page,
        limit: params.limit,
        sort: params.sort || 'desc',
        ...(params.search ? { search: params.search } : {}),
        ...(params.type ? { transaction_type: params.type } : {}),
        ...(params.status ? { status: params.status } : {}),
        ...(params.date_from ? { date_from: params.date_from } : {}),
        ...(params.date_to ? { date_to: params.date_to } : {}),
      },
    });
    return data;
  }, [API_URL]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setSubmitError('File size should not exceed 5MB');
        return;
      }
      setPaymentProof(file);
      setSubmitError('');
    }
  };

  const handleSubmitTopup = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setUploading(true);

    try {
      // Using cookie-based auth with withCredentials

      // Step 1: Upload payment proof
      const formData = new FormData();
      formData.append('file', paymentProof);

      const uploadRes = await axios.post(
        `${API_URL}/api/agent/wallet/upload-payment-proof`,
        formData,
        { withCredentials: true }
      );

      const paymentProofUrl = uploadRes.data.file_url;

      // Step 2: Submit topup request
      const topupFormData = new FormData();
      topupFormData.append('amount', topupData.amount);
      topupFormData.append('payment_proof_url', paymentProofUrl);
      topupFormData.append('bank_details', topupData.bank_details);
      topupFormData.append('notes', topupData.notes);

      await axios.post(
        `${API_URL}/api/agent/wallet/request-topup`,
        topupFormData,
        { withCredentials: true }
      );

      // Success
      setShowTopupModal(false);
      setTopupData({ amount: '', bank_details: '', notes: '' });
      setPaymentProof(null);
      fetchWalletData();
      setTxnRefresh((n) => n + 1);
      alert('Topup request submitted successfully!');
    } catch (error) {
      setSubmitError(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setUploading(false);
    }
  };

  const getStatusBadge = (status) => (
    <TxnStatusBadge status={status} />
  );

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">My Wallet</h1>
            <p className="text-gray-600 mt-1">Manage your wallet balance and transactions</p>
          </div>
          <button
            onClick={() => setShowTopupModal(true)}
            className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors shadow-sm"
          >
            <Plus size={20} />
            Request Topup
          </button>
        </div>

        {/* Balance Card */}
        <div className="bg-gradient-to-br from-teal-500 to-cyan-600 text-white rounded-2xl p-8 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <Wallet size={32} />
            </div>
            <p className="text-lg font-medium opacity-90">Current Balance</p>
          </div>
          <p className="text-5xl font-bold">₹{balance.toLocaleString()}</p>
          <p className="text-teal-50 mt-2 text-sm">Available for bookings</p>
        </div>

        {/* Credit Summary Card (only for CREDIT agents) */}
        {(['CREDIT', 'HYBRID'].includes(agent?.payment_mode) || agent?.agent_payment_capabilities?.credit_enabled) && (
          <CreditSummaryCard agent={agent} />
        )}

        {/* Pending Requests */}
        {requests.filter(r => r.status === 'pending').length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Clock className="text-yellow-600" size={20} />
              Pending Topup Requests ({requests.filter(r => r.status === 'pending').length})
            </h2>
            <div className="space-y-3">
              {requests.filter(r => r.status === 'pending').map((req, index) => (
                <div key={index} className="bg-white p-4 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800">₹{req.amount.toLocaleString()}</p>
                    <p className="text-sm text-gray-600">{new Date(req.created_at).toLocaleDateString()}</p>
                  </div>
                  {getStatusBadge(req.status)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Transaction History — table with filters + pagination */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-800">Transaction History</h2>
          </div>
          <TransactionsTable
            testidPrefix="wallet-txn"
            refreshKey={txnRefresh}
            fetchPage={fetchTxnPage}
            exportUrl={`${API_URL}/api/agent/wallet/transactions/export`}
            ledgerLabels={{
              opening: 'Opening Balance',
              closing: 'Closing Balance',
              debit:   'Total Debit (Dr)',
              credit:  'Total Credit (Cr)',
            }}
            typeOptions={[
              { value: 'credit', label: 'Credit (Topup / Refund)' },
              { value: 'debit', label: 'Debit (Booking)' },
            ]}
            statusOptions={[
              { value: 'pending', label: 'Pending' },
              { value: 'approved', label: 'Approved' },
              { value: 'completed', label: 'Completed' },
              { value: 'rejected', label: 'Rejected' },
              { value: 'cancelled', label: 'Cancelled' },
            ]}
            columns={[
              { key: 'created_at', label: 'Date',
                render: (r) => (
                  <span className="text-gray-600 whitespace-nowrap">
                    {r.created_at ? new Date(r.created_at).toLocaleString('en-IN',
                      { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })
                      : '—'}
                  </span>
                ),
                footer: () => <span className="text-gray-500">TOTAL</span> },
              { key: 'transaction_id', label: 'Txn ID / Ref',
                render: (r) => (
                  <span className="font-mono text-xs text-gray-700">
                    {r.transaction_id || r.id || '—'}
                  </span>
                ) },
              { key: 'description', label: 'Particulars',
                render: (r) => {
                  const isCommission = r.payment_method === 'agent_commission' || (r.description || '').toLowerCase().includes('commission');
                  return (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-gray-800 capitalize">{isCommission ? 'Commission credit' : (r.description || r.method || '—')}</span>
                    {r.booking_id && <BookingLink booking_id={r.booking_id} booking={r} />}
                  </div>
                  );
                } },
              { key: 'debit_amount', label: 'Debit (Dr)', align: 'right',
                render: (r) => <DebitCell amount={r.debit_amount} />,
                footer: (l) => (
                  <span className="text-red-700 font-bold">
                    ₹{Number(l.total_debit || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </span>
                ) },
              { key: 'credit_amount', label: 'Credit (Cr)', align: 'right',
                render: (r) => <CreditCell amount={r.credit_amount} />,
                footer: (l) => (
                  <span className="text-emerald-700 font-bold">
                    ₹{Number(l.total_credit || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </span>
                ) },
              { key: 'balance', label: 'Balance', align: 'right',
                render: (r) => <BalanceCell value={r.balance ?? r.balance_after} />,
                footer: (l) => (
                  <span className="text-[#0C1D36] font-bold">
                    ₹{Number(l.closing_balance || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </span>
                ) },
              { key: 'status', label: 'Status', align: 'center',
                render: (r) => <TxnStatusBadge status={r.status} /> },
            ]}
            expandRow={(r) => (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                {r.method && (
                  <div>
                    <p className="text-[10px] uppercase text-gray-400">Method</p>
                    <p className="font-medium text-gray-800 capitalize">{r.method}</p>
                  </div>
                )}
                {r.bank_details && (
                  <div>
                    <p className="text-[10px] uppercase text-gray-400">Bank Details</p>
                    <p className="text-gray-800 text-xs">{r.bank_details}</p>
                  </div>
                )}
                {r.notes && (
                  <div>
                    <p className="text-[10px] uppercase text-gray-400">Notes</p>
                    <p className="text-gray-800 text-xs">{r.notes}</p>
                  </div>
                )}
                {r.admin_notes && (
                  <div>
                    <p className="text-[10px] uppercase text-gray-400">Admin Notes</p>
                    <p className="text-gray-800 text-xs">{r.admin_notes}</p>
                  </div>
                )}
                <div>
                  <p className="text-[10px] uppercase text-gray-400">Balance Before</p>
                  <p className="text-gray-800 font-mono text-xs">
                    {r.balance_before != null ? `₹${Number(r.balance_before).toLocaleString('en-IN')}` : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-gray-400">Balance After</p>
                  <p className="text-gray-800 font-mono text-xs">
                    {r.balance_after != null ? `₹${Number(r.balance_after).toLocaleString('en-IN')}` : '—'}
                  </p>
                </div>
              </div>
            )}
          />
        </div>

        {/* Topup Modal */}
        {showTopupModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">Request Wallet Topup</h2>
                <button onClick={() => setShowTopupModal(false)}>
                  <X className="text-gray-500 hover:text-gray-700" size={24} />
                </button>
              </div>

              {submitError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                  {submitError}
                </div>
              )}

              <form onSubmit={handleSubmitTopup} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount (₹) *
                  </label>
                  <input
                    type="number"
                    required
                    min="100"
                    value={topupData.amount}
                    onChange={(e) => setTopupData({ ...topupData, amount: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter amount"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Proof (Receipt/Screenshot) *
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={handleFileChange}
                      className="hidden"
                      id="payment-proof"
                      required
                    />
                    <label
                      htmlFor="payment-proof"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <Upload className="text-gray-400" size={32} />
                      <p className="text-sm text-gray-600">
                        {paymentProof ? paymentProof.name : 'Click to upload'}
                      </p>
                      <p className="text-xs text-gray-500">JPG, PNG, PDF (Max 5MB)</p>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Transfer Details
                  </label>
                  <textarea
                    value={topupData.bank_details}
                    onChange={(e) => setTopupData({ ...topupData, bank_details: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows="2"
                    placeholder="UTR/Reference number, Bank name..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={topupData.notes}
                    onChange={(e) => setTopupData({ ...topupData, notes: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows="2"
                    placeholder="Additional information..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={uploading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:bg-gray-400"
                >
                  {uploading ? 'Submitting...' : 'Submit Request'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </AgentLayout>
  );
};

export default AgentWallet;
